import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { setSection, updateSection } from "../../../assignmentSlice";
import { getStartingIndex } from "../../../../../utils/resumeUtils";

import CommonPage from "../../common/CommonPage";

const BlankPage = ({ setCurrentStep, isPreview, isReview }) => {
  const dispatch = useDispatch();
  const { section } = useSelector((state) => state.assignment);

  const [pageList, setPageList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [userInputList, setUserInputList] = useState([]);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  const currentPage = pageList[currentIndex] || {
    question: "",
    hint: "",
    answer: "",
  };

  // Parse answer words from "word1 / word2 / word3" format
  const getAnswerWords = () => {
    if (!currentPage.answer) return [];
    return currentPage.answer.split(" / ").map((word) => word.trim());
  };

  // Parse question to get parts (text and blanks)
  const getQuestionParts = () => {
    if (!currentPage.question) return [];
    // Split by underscores (blanks are represented as ________)
    return currentPage.question.split(/_{4,}/);
  };

  useEffect(() => {
    if (pageList.length > 0) return;

    if (!isPreview && section.answer.length > 0) {
      setPageList(section.answer);
      setCurrentIndex(getStartingIndex(section.resumeIndex));
      return;
    }
    setPageList(
      section.question.data.map((question) => {
        const answerWords = question.answer ? question.answer.split(" / ").map(w => w.trim()) : [];
        return {
          ...question,
          input: Array(answerWords.length).fill(""),
          isSubmitted: false,
          isCorrect: false,
        };
      })
    );
    setCurrentIndex(0);
  }, [section]);

  useEffect(() => {
    if (currentPage.input) {
      setUserInputList(currentPage.input);
    } else {
      setUserInputList([]);
    }
  }, [currentIndex]);

  const handleChange = (index, value) => {
    const newInputList = [...userInputList];
    newInputList[index] = value;
    setUserInputList(newInputList);

    const newPageList = pageList.map((page, pageIndex) => {
      if (pageIndex === currentIndex) {
        return {
          ...page,
          input: newInputList,
        };
      }
      return page;
    });
    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));
  };

  const handleNextPage = () => {
    if (currentIndex < pageList.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      let isAllAnswered = true;

      pageList.forEach((page) => {
        if (!page.isSubmitted) {
          isAllAnswered = false;
        }
      });

      if (isAllAnswered) {
        if (!isPreview && !isReview) {
          dispatch(
            updateSection({
              sectionId: section.id,
              data: { is_finished: true, answer: pageList },
            })
          );
        }
        alert("학습이 완료되었습니다.");
        setCurrentStep(2);
      } else {
        alert("문제를 덜 풀었습니다.");
      }
    }
  };

  const handlePreviousPage = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleSubmit = () => {
    if (currentPage.isSubmitted) return;

    const answerWords = getAnswerWords();

    // Check if all inputs are filled
    if (userInputList.some((input) => !input || input.trim() === "")) {
      return alert("모든 빈칸을 채워주세요.");
    }

    // Exact match validation (case-insensitive, trim whitespace)
    const isAnswerCorrect = answerWords.every(
      (word, index) =>
        word.trim().toLowerCase() === userInputList[index].trim().toLowerCase()
    );

    setOverlayVisible(true);
    setIsCorrect(isAnswerCorrect);

    const newPageList = pageList.map((page, pageIndex) => {
      if (pageIndex === currentIndex) {
        return {
          ...page,
          input: userInputList,
          isCorrect: isAnswerCorrect,
          isSubmitted: true,
        };
      }
      return page;
    });
    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));

    setTimeout(() => {
      setOverlayVisible(false);
    }, 600);
  };

  // Render question with input fields for blanks
  const renderQuestionWithBlanks = () => {
    const parts = getQuestionParts();
    const answerWords = getAnswerWords();
    const displayInputs = currentPage.isSubmitted
      ? currentPage.isCorrect
        ? userInputList
        : answerWords
      : userInputList;

    return (
      <div style={{ fontSize: "24px", lineHeight: "2", textAlign: "center" }}>
        {parts.map((part, index) => (
          <span key={index}>
            {part}
            {index < answerWords.length && (
              <input
                type="text"
                value={displayInputs[index] || ""}
                onChange={(e) => handleChange(index, e.target.value)}
                disabled={currentPage.isSubmitted}
                style={{
                  width: `${Math.max(
                    120,
                    answerWords[index].length * 14,
                    (displayInputs[index] || "").length * 14
                  )}px`,
                  border: "none",
                  borderBottom: currentPage.isSubmitted
                    ? currentPage.isCorrect
                      ? "1px solid #4CAF50"
                      : "1px solid #f44336"
                    : "1px solid #000",
                  outline: "none",
                  textAlign: "center",
                  fontSize: "24px",
                  padding: "0 8px",
                  margin: "0 4px",
                  backgroundColor: "transparent",
                  boxSizing: "border-box",
                }}
              />
            )}
          </span>
        ))}
      </div>
    );
  };

  return (
    <CommonPage
      currentIndex={currentIndex}
      handleNextPage={handleNextPage}
      handlePreviousPage={handlePreviousPage}
      totalNo={pageList.length}
      currentPage={currentPage}
      overlayVisible={overlayVisible}
      isCorrect={isCorrect}
      handleSubmit={handleSubmit}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "40px",
          width: "90%",
        }}
      >
        <div style={{ fontSize: "20px" }}>빈칸에 알맞은 단어를 입력하시오.</div>
        <div
          style={{
            fontSize: "18px",
            textAlign: "center",
            lineHeight: "1.5",
            color: "#666",
          }}
        >
          {currentPage.hint}
        </div>
        {renderQuestionWithBlanks()}
      </div>
    </CommonPage>
  );
};

export default BlankPage;
